/**
 * Converts a hex color to RGB values string for use with rgba()
 * @param hex - The hex color string (with or without #)
 * @returns RGB values as a comma-separated string (e.g., "255, 136, 0")
 */
export function hexToRgb(hex: string): string {
  const sanitized = hex.replace('#', '');
  if (!/^[0-9A-Fa-f]{6}$/.test(sanitized)) {
    return '0, 0, 0';
  }
  const num = parseInt(sanitized, 16);
  const R = num >> 16;
  const G = (num >> 8) & 0x00ff;
  const B = num & 0x0000ff;
  return `${R}, ${G}, ${B}`;
}

/**
 * Darkens a hex color by a percentage
 * @param hex - The hex color string (with or without #)
 * @param percent - The percentage to darken (0-100)
 * @returns The darkened hex color string
 */
export function darkenColor(hex: string, percent: number): string {
  const sanitized = hex.replace('#', '');
  if (!/^[0-9A-Fa-f]{6}$/.test(sanitized)) {
    return hex; // Return original if invalid
  }
  const num = parseInt(sanitized, 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max((num >> 16) - amt, 0);
  const G = Math.max(((num >> 8) & 0x00ff) - amt, 0);
  const B = Math.max((num & 0x0000ff) - amt, 0);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

/**
 * Lightens a hex color by a percentage
 * @param hex - The hex color string (with or without #)
 * @param percent - The percentage to lighten (0-100)
 * @returns The lightened hex color string
 */
export function lightenColor(hex: string, percent: number): string {
  const sanitized = hex.replace('#', '');
  if (!/^[0-9A-Fa-f]{6}$/.test(sanitized)) {
    return hex; // Return original if invalid
  }
  const num = parseInt(sanitized, 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min((num >> 16) + amt, 255);
  const G = Math.min(((num >> 8) & 0x00ff) + amt, 255);
  const B = Math.min((num & 0x0000ff) + amt, 255);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

/**
 * Generates an array of sparkle positions for SSR-safe rendering
 * Must be called in onMount or similar client-only context
 * @param count - Number of sparkles to generate
 * @returns Array of sparkle objects with x and y percentages
 */
export function generateSparklePositions(count: number): Array<{ x: number; y: number }> {
  return Array.from({ length: count }, () => ({
    x: Math.random() * 100,
    y: Math.random() * 100
  }));
}

/**
 * Calculates the relative luminance of a hex color using ITU-R BT.709 formula
 * @param hex - The hex color string (with or without #)
 * @returns Luminance value between 0 (darkest) and 1 (lightest)
 */
export function getLuminance(hex: string): number {
  const sanitized = hex.replace('#', '');
  if (!/^[0-9A-Fa-f]{6}$/.test(sanitized)) {
    return 0;
  }
  const num = parseInt(sanitized, 16);
  const r = (num >> 16) / 255;
  const g = ((num >> 8) & 0x00ff) / 255;
  const b = (num & 0x0000ff) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Determines if a hex color is considered "light" based on luminance
 * @param hex - The hex color string (with or without #)
 * @returns true if the color is light, false if dark
 */
export function isLightColor(hex: string): boolean {
  return getLuminance(hex) > 0.5;
}
